import { Box, Button, Container, Typography, Chip, Stack } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SchoolIcon from "@mui/icons-material/School";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VerifiedIcon from "@mui/icons-material/Verified";

const TrustBadge = ({ icon, label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 1,
      borderRadius: "100px",
      bgcolor: "rgba(255,255,255,0.8)",
      border: "1px solid rgba(13,27,75,0.08)",
      backdropFilter: "blur(8px)",
      boxShadow: "0 2px 8px rgba(13,27,75,0.06)",
    }}
  >
    <Box sx={{ color: "#4F6EF7", display: "flex" }}>{icon}</Box>
    <Typography fontSize="0.82rem" fontWeight={600} color="#0D1B4B" noWrap>
      {label}
    </Typography>
  </Box>
);

const BannerSection = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "relative",
        pt: { xs: 8, md: 12 },
        pb: { xs: 8, md: 14 },
        overflow: "hidden",
        background: "linear-gradient(160deg, #F8FAFF 0%, #EEF2FF 50%, #F0F7FF 100%)",
      }}
    >
      {/* Decorative blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          right: -120,
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(79,110,247,0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -80,
          left: -80,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="space-between"
          gap={{ xs: 8, md: 8 }}
        >
          {/* ===== LEFT CONTENT ===== */}
          <Box flex={1} sx={{ maxWidth: { md: 560 } }}>
            {/* AI badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.8,
                borderRadius: "100px",
                bgcolor: "rgba(79,110,247,0.08)",
                border: "1px solid rgba(79,110,247,0.2)",
                mb: 3,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: "#4F6EF7" }} />
              <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
                AI-Powered School Management
              </Typography>
            </Box>

            {/* Headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.4rem", sm: "3rem", md: "3.6rem" },
                fontWeight: 900,
                lineHeight: 1.08,
                letterSpacing: "-1.5px",
                mb: 3,
                color: "#0D1B4B",
              }}
            >
              Empower Your School
              <br />
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                with MySchoolling
              </Box>
            </Typography>

            {/* Subheadline */}
            <Typography
              variant="h6"
              sx={{
                color: "#64748B",
                lineHeight: 1.8,
                fontWeight: 400,
                fontSize: { xs: "1rem", md: "1.1rem" },
                mb: 5,
                maxWidth: 500,
              }}
            >
              A complete digital ecosystem that simplifies school management,
              enhances parent engagement, and empowers students with
              AI-powered learning support — all in one seamless platform.
            </Typography>

            {/* CTAs */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={5}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  px: 4,
                  py: 1.8,
                  fontSize: "1rem",
                  borderRadius: "50px",
                  fontWeight: 700,
                  boxShadow: "0 12px 32px rgba(79,110,247,0.4)",
                }}
              >
                Book a Demo
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/pricing")}
                sx={{
                  px: 4,
                  py: 1.8,
                  fontSize: "1rem",
                  borderRadius: "50px",
                  fontWeight: 600,
                  borderColor: "rgba(13,27,75,0.2)",
                  color: "#0D1B4B",
                  borderWidth: "2px",
                  "&:hover": {
                    borderWidth: "2px",
                    borderColor: "#4F6EF7",
                    color: "#4F6EF7",
                    bgcolor: "rgba(79,110,247,0.04)",
                    boxShadow: "none",
                  },
                }}
              >
                View Pricing
              </Button>
            </Stack>

            {/* Trust badges */}
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              <TrustBadge icon={<SchoolIcon sx={{ fontSize: 16 }} />} label="5,000+ Students" />
              <TrustBadge icon={<PeopleAltIcon sx={{ fontSize: 16 }} />} label="95% Parent Engagement" />
              <TrustBadge icon={<VerifiedIcon sx={{ fontSize: 16 }} />} label="Trusted by Schools" />
            </Stack>
          </Box>

          {/* ===== RIGHT IMAGE ===== */}
          <Box
            flex={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ position: "relative" }}
          >
            {/* Glow behind image */}
            <Box
              sx={{
                position: "absolute",
                width: "80%",
                height: "80%",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(79,110,247,0.18) 0%, transparent 70%)",
                filter: "blur(40px)",
                zIndex: 0,
              }}
            />

            {/* Image card frame */}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                borderRadius: "28px",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.8)",
                boxShadow:
                  "0 0 0 1px rgba(79,110,247,0.1), 0 40px 100px rgba(13,27,75,0.15)",
                animation: "float 6s ease-in-out infinite",
                "@keyframes float": {
                  "0%, 100%": { transform: "translateY(0px)" },
                  "50%": { transform: "translateY(-12px)" },
                },
              }}
            >
              <Box
                component="img"
                src="/homebanner.png"
                alt="MySchoolling Dashboard"
                sx={{
                  width: "100%",
                  maxWidth: { xs: 340, sm: 420, md: 500 },
                  display: "block",
                }}
              />
            </Box>

            {/* Floating stat chip - top right */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: -16, md: 10 },
                right: { xs: 0, md: -16 },
                px: 2,
                py: 1.2,
                borderRadius: "14px",
                bgcolor: "#fff",
                boxShadow: "0 8px 24px rgba(13,27,75,0.12)",
                border: "1px solid rgba(13,27,75,0.06)",
                zIndex: 2,
                animation: "float 5s ease-in-out infinite",
                animationDelay: "1s",
              }}
            >
              <Typography fontSize="0.7rem" color="#64748b" fontWeight={500}>
                Admin workload reduced
              </Typography>
              <Typography fontSize="1.3rem" fontWeight={800} color="#0D1B4B" lineHeight={1.2}>
                70%
              </Typography>
            </Box>

            {/* Floating chip - bottom left */}
            <Box
              sx={{
                position: "absolute",
                bottom: { xs: -16, md: 20 },
                left: { xs: 0, md: -20 },
                px: 2,
                py: 1.2,
                borderRadius: "14px",
                bgcolor: "#fff",
                boxShadow: "0 8px 24px rgba(13,27,75,0.12)",
                border: "1px solid rgba(13,27,75,0.06)",
                zIndex: 2,
                animation: "float 5.5s ease-in-out infinite",
                animationDelay: "0.5s",
              }}
            >
              <Typography fontSize="0.7rem" color="#64748b" fontWeight={500}>
                AI homework help
              </Typography>
              <Typography fontSize="1.3rem" fontWeight={800} color="#4F6EF7" lineHeight={1.2}>
                24 / 7
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BannerSection;
